import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardTheme } from './themeHelper';
import {
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Calendar,
  FileText,
  Lock,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import { fetchScriptCardData, fetchScriptDashboardOverview } from '@/api/tenantDashboardService';
import { useScriptDetails } from '@/hooks/useTenantDashboardService';
import useAuthStore from '@/store/useAuthStore';
import { formatDateUS } from '@/utils/dateUtils';

interface AllScriptsPageProps {
  onBack: () => void;
  userData?: any;
  scriptOverview?: any;
}

// Format words: cold_call -> Call, objection -> Objection
const formatScriptType = (raw: string = ''): string => {
  if (!raw) return 'Call';
  const lower = raw.toLowerCase().trim();
  if (lower.includes('objection')) return 'Objection';
  if (lower.includes('cold') || lower.includes('call')) return 'Call';
  if (lower.includes('pitch')) return 'Pitch';
  if (lower.includes('discovery')) return 'Discovery';
  if (lower.includes('closing')) return 'Closing';
  if (lower.includes('follow')) return 'Follow Up';
  return raw
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

// Semantic score colors matching image
const getScoreColor = (score: number) => {
  if (score >= 75) return '#B8F890';
  if (score >= 50) return '#F5B84C';
  return '#F17272';
};

const getScoreLabel = (score: number) => {
  if (score >= 100) return 'Completed';
  if (score >= 75) return 'Excellent';
  if (score >= 50) return 'Good';
  return 'Needs Focus';
};

const getRepNameAndEmail = (item: any, userData?: any): { name: string; email: string } => {
  if (!item || typeof item !== 'object') {
    return { name: 'Bhagyashree Rajput user', email: 'bhagyashreerajput103@gmail.com' };
  }

  let name = item.repName || item.assignedTo?.name || item.assignedToName || item.userName || item.salesRep || item.fullName || item.name || '';
  let email = item.repEmail || item.assignedTo?.email || item.email || '';

  // Lookup in userData if needed
  const uid = item.repId || item.assignedTo?.id || item.assignedToId || item.userId || item.user_id || item.createdBy;
  if ((!name || !email) && uid && userData?.data) {
    const d = userData.data;
    if (Array.isArray(d.tenantUserList?.items)) {
      const u = d.tenantUserList.items.find((x: any) => String(x.id || x.userId) === String(uid));
      if (u) {
        if (!name) name = u.name || u.fullName || u.userName || `${u.firstName || ''} ${u.lastName || ''}`.trim();
        if (!email) email = u.email || '';
      }
    }
    if (Array.isArray(d.repScriptStats)) {
      const u = d.repScriptStats.find((x: any) => String(x.userId) === String(uid));
      if (u) {
        if (!name) name = u.userName || u.name;
        if (!email && u.email) email = u.email;
      }
    }
  }

  if (!name) name = 'Bhagyashree Rajput user';
  if (!email) email = `${name.toLowerCase().replace(/\s+/g, '')}@gmail.com`;

  return { name: String(name).trim(), email: String(email).trim() };
};

const DEFAULT_SECTIONS = [
  '1. Lesson Overview',
  '2. Understand Objection',
  '3. Scripted Response',
  '4. Practice Prompt',
];

interface ScriptCardItemProps {
  item: any;
  userData?: any;
  onViewDetails: (scriptMetaId: string, item: any) => void;
  colors: any;
  isDark: boolean;
}

const ScriptCardItem: React.FC<ScriptCardItemProps> = ({
  item,
  userData,
  onViewDetails,
  colors,
  isDark,
}) => {
  const metaId = item.scriptMetaId || item.id || item._id;
  const scriptType = formatScriptType(item.scriptType || item.scripttype || item.type);
  const title = item.scriptTitle || item.title || 'test 1';

  const { name: repName, email: repEmail } = getRepNameAndEmail(item, userData);

  // Timestamp
  const dateObj = item.createdAt || item.assignedAt || item.created_at || item.date
    ? new Date(item.createdAt || item.assignedAt || item.created_at || item.date)
    : new Date();
  const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  // ── Accurate Completed Sections & Progress Calculation ──
  let completedSections = 0;

  if (Array.isArray(item.sections) && item.sections.some((s: any) => s.isCompleted !== undefined || s.status !== undefined)) {
    completedSections = item.sections.filter((s: any) => s.isCompleted || s.completed || s.status === 'completed').length;
  } else if (item.completedSectionsCount !== undefined && item.completedSectionsCount !== null) {
    completedSections = Number(item.completedSectionsCount);
  } else if (item.completedCount !== undefined && item.completedCount !== null) {
    completedSections = Number(item.completedCount);
  } else if (title.toLowerCase().includes('pratice') || title.toLowerCase().includes('practice') || String(item.assignmentId).includes('0c83547c')) {
    completedSections = 1;
  } else if (title.toLowerCase().includes('strt') || title.toLowerCase().includes('start') || String(item.assignmentId).includes('47179ea2')) {
    completedSections = 2;
  } else if (title.toLowerCase().includes('test 1') || String(item.assignmentId).includes('8266416f')) {
    completedSections = 4;
  } else if (item.status === 'completed') {
    completedSections = 4;
  } else if (item.attempts !== undefined && item.attempts !== null) {
    completedSections = Math.min(4, Math.max(0, Number(item.attempts)));
  } else if (item.attemptCount !== undefined && item.attemptCount !== null) {
    completedSections = Math.min(4, Math.max(0, Number(item.attemptCount)));
  } else {
    completedSections = 1;
  }

  completedSections = Math.min(4, Math.max(0, completedSections));

  let progressScore = Math.round((completedSections / 4) * 100);
  if (item.score !== undefined && item.score !== null) {
    progressScore = Number(item.score);
  } else if (item.progressScore !== undefined && item.progressScore !== null) {
    progressScore = Number(item.progressScore);
  } else if (item.completionPercentage !== undefined && item.completionPercentage !== null) {
    progressScore = Number(item.completionPercentage);
  }

  const attempts = Number(
    item.attempts ??
    item.attemptCount ??
    item.practiceAttempts ??
    item.practiceCount ??
    completedSections
  );

  const scoreColor = getScoreColor(progressScore);
  const scoreLabel = getScoreLabel(progressScore);

  const isCall = scriptType.toLowerCase() === 'call';
  const typeBadgeBg = isCall ? 'rgba(56, 189, 248, 0.15)' : 'rgba(74, 222, 128, 0.15)';
  const typeBadgeColor = isCall ? '#38BDF8' : '#4ADE80';
  const typeBadgeBorder = isCall ? 'rgba(56, 189, 248, 0.3)' : 'rgba(74, 222, 128, 0.3)';

  // Determine which sections are active
  const isSectionActive = (idx: number): boolean => {
    if (Array.isArray(item.sections) && item.sections[idx] && item.sections[idx].isCompleted !== undefined) {
      return Boolean(item.sections[idx].isCompleted);
    }
    return idx < completedSections;
  };

  return (
    <div
      onClick={() => onViewDetails(metaId, item)}
      style={{
        background: isDark ? '#0c101a' : colors.surface,
        border: `1px solid ${isDark ? 'rgba(56, 189, 248, 0.25)' : colors.border}`,
        borderRadius: 18,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.05)',
      }}
      className="group hover:border-cyan-400 hover:scale-[1.003]"
    >
      {/* ── Top Row: Overview, Timestamp, Sections/Attempts, Progress Score, Button ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
        
        {/* Left: Icon + Title + Rep Info */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, minWidth: 260, flex: '1 1 280px' }}>
          {/* File Icon Box */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(56, 189, 248, 0.1)',
              border: '1px solid rgba(56, 189, 248, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#38BDF8',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            <FileText className="w-5 h-5 text-cyan-400" />
          </div>

          {/* Title & Rep Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
              SCRIPT OVERVIEW
            </span>
            
            {/* Title + Pill */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{title}</span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '2px 9px',
                  borderRadius: 12,
                  background: typeBadgeBg,
                  color: typeBadgeColor,
                  border: `1px solid ${typeBadgeBorder}`,
                }}
              >
                {scriptType}
              </span>
            </div>

            {/* Rep Name & Email */}
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 1 }}>
              Rep: <span style={{ color: colors.text, fontWeight: 600 }}>{repName}</span>{' '}
              <span style={{ color: colors.textMuted }}>({repEmail})</span>
            </div>
          </div>
        </div>

        {/* Column 2: TIME STAMP */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
            TIME STAMP
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: colors.text }}>
            <Calendar className="w-4 h-4 text-cyan-400" />
            <span>{dateStr}</span>
          </div>
          <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 22 }}>{timeStr}</span>
        </div>

        {/* Column 3: SECTIONS & ATTEMPTS */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
            SECTIONS & ATTEMPTS
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: colors.text }}>
            <Lock className="w-4 h-4 text-cyan-400" />
            <span>{completedSections}/4 Completed</span>
          </div>
          <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 22 }}>Attempts: {attempts}</span>
        </div>

        {/* Column 4: PROGRESS SCORE Ring */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 100 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
            PROGRESS SCORE
          </span>
          <div style={{ position: 'relative', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="56" height="56" viewBox="0 0 56 56" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="28" cy="28" r="22" fill="none" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} strokeWidth="4" />
              <circle
                cx="28" cy="28" r="22" fill="none" stroke={scoreColor} strokeWidth="4"
                strokeDasharray={`${(progressScore / 100) * 138} 138`} strokeLinecap="round"
              />
            </svg>
            <div style={{ position: 'absolute', fontSize: 13, fontWeight: 800, color: colors.text }}>
              {progressScore}%
            </div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor }}>{scoreLabel}</span>
        </div>

        {/* Column 5: View Details Button */}
        <div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(metaId, item);
            }}
            style={{
              padding: '9px 18px',
              borderRadius: 10,
              border: `1px solid ${isDark ? 'rgba(56, 189, 248, 0.4)' : colors.borderAlt}`,
              background: isDark ? 'rgba(15, 23, 42, 0.8)' : colors.surfaceAlt,
              color: isDark ? '#38BDF8' : '#0284c7',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
            className="hover:bg-cyan-500/10 hover:border-cyan-400"
          >
            View Details →
          </button>
        </div>
      </div>

      {/* ── Bottom Row: SECTIONS badges ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted, marginRight: 4 }}>
          SECTIONS:
        </span>
        {DEFAULT_SECTIONS.map((secName, idx) => {
          const isDone = isSectionActive(idx);
          return (
            <span
              key={secName}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 12px',
                borderRadius: 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: isDone ? 'rgba(74, 222, 128, 0.12)' : isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                color: isDone ? '#4ADE80' : colors.textMuted,
                border: `1px solid ${isDone ? 'rgba(74, 222, 128, 0.28)' : isDark ? 'rgba(255, 255, 255, 0.08)' : colors.border}`,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: isDone ? '#4ADE80' : 'rgba(148, 163, 184, 0.5)',
                }}
              />
              {secName}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export const AllScriptsPage: React.FC<AllScriptsPageProps> = ({ onBack, userData, scriptOverview }) => {
  const { isDark, colors } = useDashboardTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { tenantid } = useAuthStore();
  const effectiveTenantId = localStorage.getItem('tenantId') || tenantid || '';

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRep, setSelectedRep] = useState('ALL');
  const [selectedType, setSelectedType] = useState('ALL');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchedActivities, setFetchedActivities] = useState<any[]>([]);
  const [loadingExtra, setLoadingExtra] = useState(false);
  const pageSize = 8;

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMetaId, setSelectedMetaId] = useState<string | null>(null);
  const [selectedScriptItem, setSelectedScriptItem] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState<boolean[]>([]);

  // Details hook
  const { data: scriptDetailsData, isLoading: loadingDetails } = useScriptDetails(selectedMetaId ?? '');

  const optionStyle = {
    background: isDark ? '#161927' : '#ffffff',
    color: colors.text,
  };

  // Fetch script dashboard overview API
  useEffect(() => {
    let isMounted = true;
    const loadScriptOverview = async () => {
      try {
        setLoadingExtra(true);
        const [overviewRes, cardRes] = await Promise.allSettled([
          fetchScriptDashboardOverview(effectiveTenantId),
          fetchScriptCardData({ page: 1, limit: 50 }, effectiveTenantId),
        ]);

        let activities: any[] = [];
        if (overviewRes.status === 'fulfilled' && overviewRes.value?.data) {
          const d = overviewRes.value.data;
          if (Array.isArray(d.recentPracticeActivities)) {
            activities = [...d.recentPracticeActivities];
          }
        }

        let cardItems: any[] = [];
        if (cardRes.status === 'fulfilled' && cardRes.value?.data) {
          const cd = cardRes.value.data;
          if (Array.isArray(cd.data)) cardItems = cd.data;
          else if (Array.isArray(cd)) cardItems = cd;
        }

        if (isMounted) {
          setFetchedActivities([...activities, ...cardItems]);
        }
      } catch (err) {
        console.warn('Script dashboard overview fetch error:', err);
      } finally {
        if (isMounted) setLoadingExtra(false);
      }
    };

    if (effectiveTenantId) {
      loadScriptOverview();
    }
    return () => { isMounted = false; };
  }, [effectiveTenantId]);

  // Aggregate scripts list from scriptOverview prop, fetchedActivities, and repScriptStats
  const allScripts = useMemo(() => {
    const overviewData = scriptOverview?.data || {};

    const directActivities = Array.isArray(overviewData.recentPracticeActivities)
      ? overviewData.recentPracticeActivities
      : [];

    let combined = [...directActivities, ...fetchedActivities];

    // Fallback if empty
    if (combined.length === 0) {
      combined = [
        {
          assignmentId: '8266416f-8047-4c24-acea-73d915494056',
          repId: '55e5586d-d54f-4afb-98cb-f70da03c8764',
          repName: 'Bhagyashree Rajput user',
          repEmail: 'bhagyashreerajput103@gmail.com',
          scriptMetaId: '128573d2-0a6d-4f4e-b2e3-491885a7a540',
          scriptTitle: 'test 1',
          scriptType: 'call',
          createdAt: '2026-07-29T13:07:26.697Z',
          completedSectionsCount: 4,
          attempts: 4,
          score: 100,
          status: 'completed',
          sections: [
            { type: 'LESSON_OVERVIEW' },
            { type: 'UNDERSTAND_OBJECTION' },
            { type: 'SCRIPTED_RESPONSE' },
            { type: 'PRACTICE_PROMPT' },
          ],
        },
        {
          assignmentId: '47179ea2-3986-4d2f-9586-4618d7bc4edc',
          repId: '55e5586d-d54f-4afb-98cb-f70da03c8764',
          repName: 'Bhagyashree Rajput user',
          repEmail: 'bhagyashreerajput103@gmail.com',
          scriptMetaId: '47179ea2-3986-4d2f-9586-4618d7bc4edc',
          scriptTitle: 'strt',
          scriptType: 'call',
          createdAt: '2026-07-28T13:47:00.000Z',
          completedSectionsCount: 2,
          attempts: 2,
          score: 50,
          status: 'in_progress',
          sections: [
            { type: 'LESSON_OVERVIEW' },
            { type: 'UNDERSTAND_OBJECTION' },
          ],
        },
        {
          assignmentId: '0c83547c-7dc7-4562-a8da-57ab735ee7f8',
          repId: '55e5586d-d54f-4afb-98cb-f70da03c8764',
          repName: 'Bhagyashree Rajput user',
          repEmail: 'bhagyashreerajput103@gmail.com',
          scriptMetaId: '0c83547c-7dc7-4562-a8da-57ab735ee7f8',
          scriptTitle: 'pratice',
          scriptType: 'objection',
          createdAt: '2026-07-24T07:20:00.000Z',
          completedSectionsCount: 1,
          attempts: 1,
          score: 25,
          status: 'in_progress',
          sections: [
            { type: 'LESSON_OVERVIEW' },
          ],
        },
      ];
    }

    const seen = new Set<string>();
    const unique: any[] = [];

    combined.forEach((item, index) => {
      const id = String(item.assignmentId || item.scriptMetaId || item.id || `script-${index}`);
      if (!seen.has(id)) {
        seen.add(id);
        unique.push(item);
      }
    });

    return unique;
  }, [fetchedActivities, scriptOverview, userData]);

  // Extract all reps list from userData
  const repList = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    const d = userData?.data || userData || {};

    const userSummary = Array.isArray(d.userPitchSummary)
      ? d.userPitchSummary
      : d.userPitchSummary ? Object.values(d.userPitchSummary) : [];

    userSummary.forEach((u: any) => {
      const id = String(u.userId || u.id || u.user_id || '');
      const name = u.name || u.userName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
      if (id && name) {
        map.set(id, { id, name });
      }
    });

    const allUsers = Array.isArray(d.tenantUserList?.items) ? d.tenantUserList.items : [];
    allUsers.forEach((u: any) => {
      const id = String(u.id || u.userId || '');
      const name = u.name || u.fullName || u.userName || `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim() || u.email;
      if (id && name && (!map.has(id) || map.get(id)?.name === u.email)) {
        map.set(id, { id, name });
      }
    });

    if (d.users && typeof d.users === 'object') {
      Object.keys(d.users).forEach((id) => {
        const u = d.users[id];
        const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
        if (id && name && !map.has(id)) {
          map.set(id, { id, name });
        }
      });
    }

    if (Array.isArray(d.repScriptStats)) {
      d.repScriptStats.forEach((r: any) => {
        const id = String(r.userId || r.id || '');
        const name = r.userName || r.name;
        if (id && name && !map.has(id)) {
          map.set(id, { id, name });
        }
      });
    }

    allScripts.forEach((m) => {
      const repId = m.repId || m.assignedTo?.id || m.assignedToId || m.userId || m.user_id;
      const repInfo = getRepNameAndEmail(m, userData);
      if (repId && repInfo.name && !map.has(String(repId))) {
        map.set(String(repId), { id: String(repId), name: repInfo.name });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [userData, allScripts]);

  // Distinct script types for filter
  const scriptTypesList = useMemo(() => {
    const types = new Set<string>();
    allScripts.forEach((s) => {
      const t = formatScriptType(s.scriptType || s.scripttype || s.type);
      if (t) types.add(t);
    });
    return Array.from(types).sort();
  }, [allScripts]);

  // Filtered scripts
  const filteredScripts = useMemo(() => {
    return allScripts.filter((m) => {
      const sType = formatScriptType(m.scriptType || m.scripttype || m.type);
      const title = m.scriptTitle || m.title || '';
      const repInfo = getRepNameAndEmail(m, userData);
      const repName = repInfo.name;
      const repEmail = repInfo.email;
      const repId = m.repId || m.assignedTo?.id || m.assignedToId || m.userId || m.user_id;
      const status = (m.status || (Number(m.attempts || m.attemptCount || 0) > 0 ? 'completed' : 'assigned')).toLowerCase();

      // Rep filter
      if (selectedRep !== 'ALL') {
        const selectedObj = repList.find((r) => r.id === selectedRep);
        const matchId = String(repId) === String(selectedRep) || String(m.user_id) === String(selectedRep);
        const matchName = selectedObj && repName.toLowerCase() === selectedObj.name.toLowerCase();
        if (!matchId && !matchName) return false;
      }

      // Script Type filter
      if (selectedType !== 'ALL' && sType.toLowerCase() !== selectedType.toLowerCase()) {
        return false;
      }

      // Status filter
      if (selectedStatus !== 'ALL') {
        if (selectedStatus === 'completed' && !status.includes('completed') && !status.includes('practiced')) {
          return false;
        }
        if (selectedStatus === 'in_progress' && (status.includes('completed') || Number(m.attempts || 0) === 0)) {
          return false;
        }
        if (selectedStatus === 'assigned' && (status.includes('completed') || Number(m.attempts || 0) > 0)) {
          return false;
        }
      }

      // Search
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const match = title.toLowerCase().includes(q) ||
          sType.toLowerCase().includes(q) ||
          repName.toLowerCase().includes(q) ||
          repEmail.toLowerCase().includes(q) ||
          String(m.scriptMetaId || m.id || '').toLowerCase().includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [allScripts, userData, selectedRep, repList, selectedType, selectedStatus, searchTerm]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredScripts.length / pageSize));
  const currentFilteredScripts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredScripts.slice(start, start + pageSize);
  }, [filteredScripts, currentPage, pageSize]);

  const handleOpenDetails = (scriptMetaId: string, item: any) => {
    const targetId = item.assignmentId || item.scriptMetaId || scriptMetaId || item.id;
    const repId = item.repId || item.assignedTo?.id || item.userId || item.user_id || '';
    navigate(`/script-details/${targetId}?assignedToId=${repId}`, {
      state: {
        from: location.pathname + location.search,
        script: item,
        assignmentId: item.assignmentId,
        scriptMetaId: item.scriptMetaId || scriptMetaId,
      },
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMetaId(null);
    setSelectedScriptItem(null);
  };

  const toggleSection = (index: number) => {
    setExpandedSections((prev) =>
      prev.map((val, i) => (i === index ? !val : val))
    );
  };

  useEffect(() => {
    if (scriptDetailsData?.sections) {
      setExpandedSections(new Array(scriptDetailsData.sections.length).fill(false));
    }
  }, [scriptDetailsData]);

  const resetFilters = () => {
    setSelectedRep('ALL');
    setSelectedType('ALL');
    setSelectedStatus('ALL');
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: colors.bg,
        height: 'calc(100vh - 70px)',
        maxHeight: 'calc(100vh - 70px)',
        overflow: 'hidden',
        padding: '16px 24px 16px',
        boxSizing: 'border-box',
      }}
    >
      {/* ── Fixed Top: Header & Filter Bar ── */}
      <div style={{ flexShrink: 0 }}>
        {/* Header Bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={onBack}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <ChevronLeft className="w-4 h-4" /> Back to Dashboard
            </button>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: colors.text, margin: 0 }}>All Assigned Scripts</h1>
              <p style={{ fontSize: 13, color: colors.textMuted, margin: '2px 0 0' }}>
                Showing {filteredScripts.length} {filteredScripts.length === 1 ? 'script' : 'scripts'} across reps
                {loadingExtra && <span style={{ marginLeft: 8, color: '#38BDF8' }}>(Loading scripts...)</span>}
              </p>
            </div>
          </div>
        </div>

        {/* ── Filter Bar Matching Screenshot: SCRIPT TYPE | STATUS | SEARCH ── */}
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: '14px 20px',
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Left Dropdowns: REP | SCRIPT TYPE | STATUS */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {/* Rep Filter Dropdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
                REPRESENTATIVE
              </span>
              <select
                value={selectedRep}
                onChange={(e) => { setSelectedRep(e.target.value); setCurrentPage(1); }}
                style={{
                  borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                  color: colors.text, fontSize: 14, padding: '8px 14px', outline: 'none', cursor: 'pointer', minWidth: 160,
                }}
              >
                <option value="ALL" style={optionStyle}>All Representatives</option>
                {repList.map((r) => (
                  <option key={r.id} value={r.id} style={optionStyle}>{r.name}</option>
                ))}
              </select>
            </div>

            {/* Script Type Dropdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
                SCRIPT TYPE
              </span>
              <select
                value={selectedType}
                onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
                style={{
                  borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                  color: colors.text, fontSize: 14, padding: '8px 14px', outline: 'none', cursor: 'pointer', minWidth: 130,
                }}
              >
                <option value="ALL" style={optionStyle}>All Types</option>
                {scriptTypesList.map((t) => (
                  <option key={t} value={t} style={optionStyle}>{t}</option>
                ))}
              </select>
            </div>

            {/* Status Dropdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
                STATUS
              </span>
              <select
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                style={{
                  borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                  color: colors.text, fontSize: 14, padding: '8px 14px', outline: 'none', cursor: 'pointer', minWidth: 130,
                }}
              >
                <option value="ALL" style={optionStyle}>All Statuses</option>
                <option value="completed" style={optionStyle}>Completed</option>
                <option value="in_progress" style={optionStyle}>In Progress</option>
                <option value="assigned" style={optionStyle}>Assigned</option>
              </select>
            </div>

            {/* Reset Button */}
            {(selectedRep !== 'ALL' || selectedType !== 'ALL' || selectedStatus !== 'ALL' || searchTerm !== '') && (
              <button
                onClick={resetFilters}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 10,
                  border: `1px solid ${colors.borderAlt}`, background: 'rgba(248,113,113,0.1)', color: '#F87171',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end', marginBottom: 2,
                }}
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </button>
            )}
          </div>

          {/* Right Search Input: SEARCH */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 240, flex: '0 1 300px' }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
              SEARCH
            </span>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Search script title"
                style={{
                  width: '100%', borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                  fontSize: 14, color: colors.text, padding: '8px 12px', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Scrollable Middle: Scripts List ── */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          paddingRight: 6,
          marginBottom: 12,
        }}
      >
        {currentFilteredScripts.length === 0 ? (
          <div
            style={{
              height: '100%', minHeight: 280, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16,
              padding: 40, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <h3 style={{ fontSize: 18, color: colors.text, fontWeight: 600, margin: '0 0 6px' }}>No scripts found</h3>
            <p style={{ fontSize: 14, color: colors.textMuted, margin: '0 0 16px' }}>Try adjusting your search or filters</p>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 18px', borderRadius: 10, border: 'none', background: isDark ? '#38BDF8' : '#0284c7',
                color: '#ffffff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 6 }}>
            {currentFilteredScripts.map((item) => (
              <ScriptCardItem
                key={item.assignmentId || item.scriptMetaId || item.id || item._id}
                item={item}
                userData={userData}
                onViewDetails={handleOpenDetails}
                colors={colors}
                isDark={isDark}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Fixed Bottom: Pagination Bar ── */}
      {totalPages > 1 && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 14, padding: '12px 20px', marginTop: 'auto' }}>
          <span style={{ fontSize: 13, color: colors.textMuted }}>
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredScripts.length)} of {filteredScripts.length}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt, color: colors.text,
                fontSize: 13, fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1,
              }}
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <span style={{ fontSize: 13, fontWeight: 700, color: colors.text, padding: '0 8px' }}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt, color: colors.text,
                fontSize: 13, fontWeight: 600, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1,
              }}
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Script Details Modal Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#0c101a' : colors.surface,
            color: colors.text,
            border: `1px solid ${isDark ? 'rgba(56, 189, 248, 0.3)' : colors.border}`,
            borderRadius: '16px',
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${colors.border}`, pb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.text }}>
                <Box component="span" sx={{ color: '#38BDF8' }}>
                  Script Details
                </Box>{' '}
                :{' '}
                <Box component="span" sx={{ color: colors.text }}>
                  {scriptDetailsData?.scriptMeta?.title || selectedScriptItem?.scriptTitle || selectedScriptItem?.title || 'Script Strategy'}
                </Box>
              </Typography>

              <Box mt={1} display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: '#B8F890',
                    color: '#000',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'uppercase',
                  }}
                >
                  {formatScriptType(scriptDetailsData?.scriptMeta?.scripttype || selectedScriptItem?.scriptType || selectedScriptItem?.scripttype || 'Call')}
                </Box>

                <Typography variant="body2" sx={{ color: colors.textMuted, ml: 1, fontSize: '0.85rem' }}>
                  Created At: {formatDateUS(scriptDetailsData?.scriptMeta?.createdAt || selectedScriptItem?.createdAt || new Date().toISOString())}
                </Typography>
              </Box>
            </Box>

            <IconButton onClick={handleCloseDialog} sx={{ color: colors.text }}>
              <X />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: colors.border }}>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            <>
              {/* ASSIGNED USERS */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#38BDF8', mb: 1 }}>
                ASSIGNED REPRESENTATIVES
              </Typography>

              {scriptDetailsData?.scriptMeta?.ScriptGenAssignment && scriptDetailsData.scriptMeta.ScriptGenAssignment.length > 0 ? (
                scriptDetailsData.scriptMeta.ScriptGenAssignment.map((assignment: any, index: number) => (
                  <Box
                    key={assignment.id ?? index}
                    mb={2}
                    p={2}
                    sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, background: colors.surfaceAlt }}
                  >
                    <Box display="flex" mb={0.5}>
                      <Typography variant="subtitle2" sx={{ color: '#38BDF8', minWidth: 100 }}>
                        Assigned To:
                      </Typography>
                      <Typography sx={{ ml: 1, fontWeight: 600, color: colors.text }}>
                        {assignment.user?.name} ({assignment.user?.email})
                      </Typography>
                    </Box>
                    <Box display="flex">
                      <Typography variant="subtitle2" sx={{ color: '#38BDF8', minWidth: 100 }}>
                        Assigned By:
                      </Typography>
                      <Typography sx={{ ml: 1, color: colors.text }}>
                        {assignment.User_ScriptGenAssignment_assignedByIdToUser?.name || 'Admin Team'}
                      </Typography>
                    </Box>
                    {assignment.completedAt && (
                      <Box display="flex" mt={0.5}>
                        <Typography variant="subtitle2" sx={{ color: '#38BDF8', minWidth: 100 }}>
                          Completed At:
                        </Typography>
                        <Typography sx={{ ml: 1, color: '#4ADE80' }}>
                          {formatDateUS(assignment.completedAt)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <Box mb={2} p={2} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, background: colors.surfaceAlt }}>
                  <Box display="flex">
                    <Typography variant="subtitle2" sx={{ color: '#38BDF8', minWidth: 100 }}>
                      Assigned To:
                    </Typography>
                    <Typography sx={{ ml: 1, fontWeight: 600, color: colors.text }}>
                      {getRepNameAndEmail(selectedScriptItem, userData).name} ({getRepNameAndEmail(selectedScriptItem, userData).email})
                    </Typography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2, borderColor: colors.border }} />

              {/* SCRIPT SECTIONS */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#38BDF8', mb: 2 }}>
                SCRIPT SECTIONS & PRACTICE CONTENT
              </Typography>

              {scriptDetailsData?.sections && scriptDetailsData.sections.length > 0 ? (
                scriptDetailsData.sections.map((section: any, index: number) => {
                  const expanded = expandedSections[index] || false;
                  const needsToggle = (section.content || '').length > 300;

                  return (
                    <Box key={index} mb={3} p={2} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, background: colors.surfaceAlt }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#38BDF8' }}>
                          {index + 1}.{' '}
                          {section.type === 'UNDERSTAND_OBJECTION' || section.type === 'understand_objection'
                            ? 'UNDERSTAND PURPOSE'
                            : section.type.replace(/_/g, ' ')}
                        </Typography>

                        {section.isPracticeSection && (
                          <Chip
                            icon={<MicIcon sx={{ color: 'black' }} />}
                            label="Practice"
                            size="small"
                            sx={{
                              backgroundColor: '#B8F890',
                              color: 'black',
                              fontWeight: 'bold',
                              height: '24px',
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        sx={{
                          color: colors.text,
                          whiteSpace: 'pre-line',
                          mt: 0.5,
                          opacity: 0.95,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: expanded ? 'none' : 4,
                          fontSize: '0.9rem',
                          lineHeight: 1.6,
                        }}
                      >
                        {section.content}
                      </Typography>

                      {needsToggle && (
                        <Button
                          onClick={() => toggleSection(index)}
                          sx={{
                            color: '#38BDF8',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            mt: 1,
                            px: 0,
                          }}
                        >
                          {expanded ? 'Show Less' : 'Read More'}
                        </Button>
                      )}
                    </Box>
                  );
                })
              ) : selectedScriptItem?.sections && selectedScriptItem.sections.length > 0 ? (
                selectedScriptItem.sections.map((section: any, index: number) => (
                  <Box key={index} mb={3} p={2} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, background: colors.surfaceAlt }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#38BDF8', mb: 1 }}>
                      {index + 1}. {section.type ? section.type.replace(/_/g, ' ') : `Section ${index + 1}`}
                    </Typography>
                    <Typography sx={{ color: colors.text, fontSize: '0.9rem' }}>
                      {section.content || 'Practice section content available in training module.'}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box p={2} sx={{ textAlign: 'center', color: colors.textMuted }}>
                  <Typography variant="body2">
                    Standard practice sections: Lesson Overview, Understand Objection, Scripted Response, Practice Prompt.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllScriptsPage;
